import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { PdpScoreStatus, type PdpStatus } from '@vizit/api';
import { Badge, BadgeLevel, BadgeShape, Cell, Image, Row, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import type { RowProps } from '@vizit/atoms';
import { FEATURE_FLAGS, RETAILER_CONFIG_MAP, type RetailerKey, isOtherRetailerRegion } from '@vizit/constants';
import { useFeatureFlag, useShowUniversalBenchmarks } from '@vizit/feature-flags';

import { cva, cx } from '@vizit/theme';
import * as React from 'react';
import { type RowAction, RowActionButtons } from './product-listing-row.action-buttons';
import { ProductListingRowChange } from './product-listing-row.change';
import { rowClasses } from './product-listing-row.classes';
import { ProductListingRowLoading } from './product-listing-row.loading';
import { ProductListingRowRetailer } from './product-listing-row.retailer';
import { ProductListingRowScore } from './product-listing-row.score';
import { ProductListingRowYourScore } from './product-listing-row.your-score';

const contentClasses = cva({
  base: 'transition-opacity duration-150',
  variants: {
    disabled: {
      false: 'opacity-100',
      true: 'opacity-50',
    },
  },
});

/**
 * Product data interface for the table
 */
export interface ProductListingData {
  /**
   * The product ID
   *
   * @required
   * @type string
   */
  id: string;
  /**
   * The product image source URL
   *
   * @required
   * @type string | null
   */
  imageSrc: string | null;
  /**
   * The product image thumbnail source URL
   * (imageSrc to be used as a fallback)
   *
   * @optional
   * @type string | null
   */
  imageThumbnailSrc?: string | null;
  /**
   * The product name
   *
   * @required
   * @type string
   */
  productName: string;
  /**
   * The product item identifier (ASIN for Amazon, Product ID for Walmart)
   *
   * @required
   * @type string
   */
  asin: string;
  /**
   * The retailer key (e.g. 'amazon_us', 'walmart_us')
   *
   * @optional
   * @type string
   */
  retailer?: string;
  /**
   * Label of the benchmark the page is scored against, shown in the Other retailer's tooltip
   *
   * @optional
   * @type string | null
   */
  benchmarkLabel?: string | null;
  /**
   * The retailer the page is planned for, when it was staged for the Other retailer. Not rendered in the table.
   *
   * @optional
   * @type string | null
   */
  customRetailer?: string | null;
  /**
   * The Vizit listing score (0-100)
   *
   * @optional
   * @type number | null
   */
  score?: number | null;
  /**
   * The current score state of the product
   *
   * @optional
   * @type ScoreStatus | null
   */
  scoreStatus: PdpScoreStatus | null;
  /**
   * The source type of the product (manual or scraped)
   *
   * @optional
   * @type 'manual' | 'scraped' | null
   */
  sourceType?: 'manual' | 'scraped' | null;
  /**
   * The status of the product
   *
   * @optional
   * @type PdpStatus | null
   */
  status: PdpStatus | null;
  /**
   * The Vizit-modified listing score after user edits (0-100)
   *
   * @optional
   * @type number | null
   */
  yourScore?: number | null;
  /**
   * The formula withheld this row's score (formula-served, no listing score, not in flight).
   * The score cell then shows an "Unavailable" marker instead of a number.
   *
   * @optional
   * @type boolean
   */
  isScoreWithheld?: boolean;
  /**
   * The score change/delta between live score and your score
   *
   * @optional
   * @type number | null
   */
  scoreChange?: number | null;
  /**
   * The last time the product was refreshed
   *
   * @optional
   * @type Date | null
   */
  lastRefreshed: Date | null;
  /**
   * The last time the product was scraped
   *
   * @optional
   * @type Date | null
   */
  lastScraped: Date | null;
  /**
   * The date the product was created
   *
   * @required
   * @type Date
   */
  createdAt: Date;
}

interface ProductListingRowProps extends RowProps<object> {
  item: ProductListingData;
  /**
   * Callback fired when the row is clicked
   *
   * @optional
   * @type (pdpId: string) => void
   */
  onRowClick?: (pdpId: string) => void;
  /**
   * Array of action buttons to display on row hover
   *
   * @optional
   * @type RowAction[]
   */
  actions?: RowAction[];
  /**
   * Additional CSS classes to apply to the component
   *
   * @optional
   * @type string
   */
  className?: string;
  /**
   * Whether the row is in a loading state
   *
   * @optional
   * @type boolean
   * @default false
   */
  isLoading?: boolean;
  /**
   * Ref to the row element
   *
   * @optional
   * @type React.RefObject<Element>
   */
  ref?: (element: HTMLTableRowElement) => void;
}

/**
 * ProductListingRow displays product information as a table row with multiple cells:
 * image + product info, live score, your score, score change delta, and optional actions. Supports different states
 * (processing, error, etc.) and interactive hover/focus states.
 *
 * Designed to work with TanStack Table and React Aria Table components.
 *
 * @param props - The props for the ProductListingRow component
 * @example
 * // Used within a Table component
 * <Table>
 *   <TableHeader>
 *     <Column>Product</Column>
 *     <Column>Score</Column>
 *   </TableHeader>
 *   <TableBody>
 *     <ProductListingRow
 *       item={{
 *         id: '123',
 *         productName: 'Wireless Earbuds with ChatGPT Integration',
 *         asin: 'B0C34GV9ZC',
 *         imageSrc: '/product-image.jpg',
 *         scoreStatus: 'SCORED',
 *         sourceType: 'manual',
 *       }}
 *       actions={[
 *         {
 *           id: 'refresh',
 *           icon: <RefreshIcon />,
 *           label: 'Refresh',
 *           onClick: handleRefresh,
 *           tooltip: 'Refresh product data'
 *         }
 *       ]}
 *     />
 *   </TableBody>
 * </Table>
 */
const ProductListingRow = React.memo<ProductListingRowProps>((props) => {
  const { item, onRowClick, actions, className, isLoading, ref, ...other } = props;

  const { id: pdpId, productName, asin, retailer, imageSrc, imageThumbnailSrc, scoreStatus, sourceType } = item;
  const { workspaceSlug } = useParams({ strict: false });

  const isRetailerEnabled = useFeatureFlag(FEATURE_FLAGS.APRIL_SCORING_CHANGES, false);
  const idLabel = isRetailerEnabled ? (RETAILER_CONFIG_MAP[retailer as RetailerKey]?.itemIdLabel ?? 'ID') : 'ID';

  const showUniversalBenchmarks = useShowUniversalBenchmarks();
  const isOtherRetailer = isRetailerEnabled && showUniversalBenchmarks && isOtherRetailerRegion(retailer);
  const navigate = useNavigate();

  const productLink = React.useMemo(
    () =>
      ({
        to: '/w/$workspaceSlug/library/products/$pdpId',
        params: { workspaceSlug, pdpId },
        search: { products: 'open' },
      }) as const,
    [workspaceSlug, pdpId],
  );

  const handleOpenProduct = React.useCallback(() => navigate(productLink), [navigate, productLink]);

  const handleAction = React.useCallback(() => {
    if (onRowClick && pdpId) {
      onRowClick(pdpId);
    }
  }, [onRowClick, pdpId]);

  if (isLoading) {
    return <ProductListingRowLoading showActionColumn={!!actions?.length} />;
  }

  const showLoading =
    isLoading || (scoreStatus !== PdpScoreStatus.Scored && scoreStatus !== PdpScoreStatus.RefreshPending);

  return (
    <Row
      className={cx(rowClasses(), className)}
      aria-label={`Product: ${productName}, ${idLabel}: ${asin}`}
      data-item-id={asin}
      onAction={handleAction}
      ref={ref}
      {...other}
    >
      <Cell>
        <Link {...productLink} className="block py-5 px-12">
          <div className="flex items-center gap-6">
            <div className={cx('flex-shrink-0 w-28 h-28', contentClasses())}>
              <Image
                src={imageThumbnailSrc ?? imageSrc}
                fallbackSrc={imageSrc}
                isBroken={!imageSrc && !showLoading}
                name={productName}
                shape="rectangle"
                contain={false}
                className="w-full h-full"
                imageClassName="object-cover"
                isThumbnail
              />
            </div>

            <div className={cx('flex flex-col justify-center gap-1 min-w-0 max-w-[720px]', contentClasses())}>
              <Text
                size={TextSize.Md}
                weight={TextWeight.Medium}
                className="group-hover:underline"
                maxLines={2}
                showEllipsis={true}
              >
                {productName}
              </Text>
              {isOtherRetailer ? (
                <Badge className="w-fit" level={BadgeLevel.Default} shape={BadgeShape.Box}>
                  Staged page
                </Badge>
              ) : sourceType === 'manual' ? (
                <Badge className="w-fit" level={BadgeLevel.Default} shape={BadgeShape.Box}>
                  Pre-launch
                </Badge>
              ) : (
                <Text size={TextSize.Sm} color={TextColor.Secondary} showEllipsis={true}>
                  {asin}
                </Text>
              )}
            </div>
          </div>
        </Link>
      </Cell>

      {isRetailerEnabled && (
        <Cell>
          <Link {...productLink} className="flex items-center justify-center py-5 px-12 text-tertiary">
            <ProductListingRowRetailer
              retailer={retailer}
              benchmarkLabel={item.benchmarkLabel}
              onPress={onRowClick ? handleAction : handleOpenProduct}
            />
          </Link>
        </Cell>
      )}

      <Cell>
        <Link
          {...productLink}
          className="flex items-center justify-center py-5 px-12"
          data-testid="product-row-live-score"
        >
          <ProductListingRowScore item={item} isStaged={isOtherRetailer} />
        </Link>
      </Cell>

      <Cell>
        <Link
          {...productLink}
          className="flex items-center justify-center py-5 px-12"
          data-testid="product-row-your-score"
        >
          <ProductListingRowYourScore item={item} />
        </Link>
      </Cell>

      <Cell>
        <Link {...productLink} className="block py-5 px-12">
          <ProductListingRowChange scoreChange={item.scoreChange} />
        </Link>
      </Cell>

      {actions && actions.length > 0 && (
        <Cell className="py-5 px-12">
          <div className="flex items-center justify-end">
            <RowActionButtons actions={actions} item={item} />
          </div>
        </Cell>
      )}
    </Row>
  );
});

ProductListingRow.displayName = 'ProductListingRow';

export type { RowAction } from './product-listing-row.action-buttons';
export { ProductListingRow };
export type { ProductListingRowProps };
